'use client';

import { ArrowRight } from 'lucide-react';
import { type ProtegeColumn, protegeColumns } from '~/@types/interface';
import Button from '~/components/Button';
import Select from '~/components/Select';
import { ConnectIconTile } from './ConnectIconTile';

export type SmartsheetColumnOption = {
  id: string;
  title: string;
};

export function SmartsheetFieldMappingStep({
  columns,
  mapping,
  onMappingChange,
  onContinue,
  onBack,
  isContinuing = false,
}: {
  columns: SmartsheetColumnOption[];
  mapping: Record<ProtegeColumn, string>;
  onMappingChange: (next: Record<ProtegeColumn, string>) => void;
  onContinue: () => void;
  onBack: () => void;
  isContinuing?: boolean;
}) {
  const columnOptions = columns.map((column) => ({
    value: column.id,
    label: column.title,
  }));
  const usedIds = Object.values(mapping).filter(Boolean);
  const hasEmpty = protegeColumns.some((field) => !mapping[field]);
  const hasDuplicate = usedIds.length !== new Set(usedIds).size;
  const canContinue = !hasEmpty && !hasDuplicate && !isContinuing;

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-foreground text-lg font-semibold tracking-tight">Map Fields</h3>
        <p className="text-muted-foreground mt-1 text-sm leading-5">
          Match each Smartsheet column to the Protege field it fills.
        </p>
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-x-2 gap-y-2">
        <div className="flex items-center gap-2">
          <ConnectIconTile src="/smartsheetLogo.png" alt="" size="sm" />
          <span className="text-foreground text-[13px] font-medium tracking-tight">Smartsheet</span>
        </div>
        <span aria-hidden className="size-4" />
        <div className="flex items-center gap-2">
          <ConnectIconTile src="/logo.png" alt="" size="sm" className="bg-primary ring-0" />
          <span className="text-foreground text-[13px] font-medium tracking-tight">Protege</span>
        </div>

        {protegeColumns.map((field) => {
          const value = mapping[field] ?? '';
          const duplicate =
            Boolean(value) && usedIds.filter((id) => id === value).length > 1;

          return (
            <div key={field} className="contents">
              <Select
                className="min-w-0"
                placeholder="Column"
                options={columnOptions}
                value={value}
                error={duplicate ? 'Already mapped to another field' : undefined}
                onChange={(e) =>
                  onMappingChange({
                    ...mapping,
                    [field]: e.target.value,
                  })
                }
              />
              <span className="flex h-9 items-center">
                <ArrowRight
                  className="text-muted-foreground size-4 shrink-0"
                  strokeWidth={2}
                  aria-hidden
                />
              </span>
              <div className="bg-muted text-foreground flex h-9 items-center rounded-md px-3 text-sm">
                {field}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-col gap-2">
        <Button
          size="sm"
          className="w-full"
          label="Continue"
          disabled={!canContinue}
          isLoading={isContinuing}
          onClick={onContinue}
        />
        <Button
          size="sm"
          variant="outline"
          className="w-full"
          label="Back"
          disabled={isContinuing}
          onClick={onBack}
        />
      </div>
    </div>
  );
}
